import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { ResponsiveRowTableCards, type ResponsiveRowColumn } from '@gastrosoft/shared/ui';
import {
  GetPlanDetailDocument,
  GetProductsListDocument,
  UpdatePlanDocument,
  type GetPlanDetailQuery,
  type GetProductsListQuery,
  type Product,
  type UpdatePlanMutation,
  type UpdatePlanMutationVariables,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import { productTypeLabel } from '@/utils/productTypeLabel';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

//   type AddProductToPlanMutation = { addProductToPlan: AddProductToPlanPayload };
// type RemoveProductFromPlanMutation = { removeProductFromPlan: RemoveProductFromPlanPayload };

// type PlanProductRow = NonNullable<
//   NonNullable<NonNullable<GetPlanDetailQuery['plan']>['planProducts']>[number]['product']
// >;

export default function PlanDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [contractTermMonths, setContractTermMonths] = useState('');
  const [amountNet, setAmountNet] = useState('');
  const [amountGross, setAmountGross] = useState('');
  const [productIdToAdd, setProductIdToAdd] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetPlanDetailQuery>(GetPlanDetailDocument, {
    variables: { id: id! },
    skip: !id,
  });
  const [getProducts, { data: productsData }] =
    useLazyQuery<GetProductsListQuery>(GetProductsListDocument);
  const plan = data?.plan;

  useEffect(() => {
    if (!plan) return;
    setName(plan.name);
    setCode(plan.code);
    setContractTermMonths(String(plan.contractTermMonths));
    setAmountNet(String(plan.amountNet));
    setAmountGross(String(plan.amountGross));
    getProducts({ variables: { where: { sku: { in: plan.skus } } } });
  }, [getProducts, plan]);

  const [updatePlan, { loading: saving }] = useMutation<
    UpdatePlanMutation,
    UpdatePlanMutationVariables
  >(UpdatePlanDocument);

  // const [deletePlan, { loading: deleting }] = useMutation<
  //   DeletePlanMutation,
  //   DeletePlanMutationVariables
  // >(DeletePlanDocument);
  // const [addProductToPlan, { loading: adding }] = useMutation<
  //   AddProductToPlanMutation,
  //   { input: AddProductToPlanInput }
  // >(AddProductToPlanDocument);
  // const [removeProductFromPlan, { loading: removing }] = useMutation<
  //   RemoveProductFromPlanMutation,
  //   { input: RemoveProductFromPlanInput }
  // >(RemoveProductFromPlanDocument);

  const handleSave = async () => {
    if (!id) return;
    const net = parseFloat(amountNet.replace(',', '.'));
    const gross = parseFloat(amountGross.replace(',', '.'));
    const months = parseInt(contractTermMonths, 10);
    if (Number.isNaN(net) || Number.isNaN(gross) || Number.isNaN(months)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await updatePlan({
        variables: {
          input: { id, name, code, contractTermMonths: months, amountNet: net, amountGross: gross },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updatePlan);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleAddProductToPlan = async () => {
    if (!id) return;
    await updatePlan({
      variables: {
        input: { id, skus: [...(plan?.skus ?? []), productIdToAdd] },
      },
    });
  };

  const handleRemoveProductFromPlan = async (sku: string) => {
    if (!id) return;
    await updatePlan({
      variables: {
        input: { id, skus: plan?.skus?.filter((s) => s !== sku) ?? [] },
      },
    });
  };

  // const handleDelete = async () => {
  //   if (!id) return;
  //   try {
  //     const res = await deletePlan({ variables: { input: { id } } });
  //     const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
  //     if (top?.length) {
  //       showError(top.map((e) => e.message).join(' '));
  //       return;
  //     }
  //     const pe = errorsFromPayload(res.data?.deletePlan);
  //     if (pe || !res.data?.deletePlan?.boolean) {
  //       showError(pe || t('errors.generic'));
  //       return;
  //     }
  //     showSuccess(t('common.deleted'));
  //     navigate('/plans');
  //   } catch (e) {
  //     showError(e instanceof Error ? e.message : t('errors.generic'));
  //   }
  // };

  const productColumns: ResponsiveRowColumn<Product>[] = [
    {
      id: 'name',
      header: t('common.name'),
      render: (p: Product) => <Typography variant="body2">{p.name}</Typography>,
    },
    {
      id: 'number',
      header: t('masterData.productNumber'),
      render: (p: Product) => <Typography variant="body2">{p.number}</Typography>,
    },
    {
      id: 'sku',
      header: t('masterData.sku'),
      render: (p: Product) => <Typography variant="body2">{p.sku ?? '–'}</Typography>,
    },
    {
      id: 'type',
      header: t('common.type'),
      render: (p: Product) => (
        <Typography variant="body2">{productTypeLabel(t, p.type)}</Typography>
      ),
    },
    ...(superAdmin
      ? [
          {
            id: 'delete',
            header: t('common.delete'),
            align: 'right' as const,
            render: (p: Product) => (
              <IconButton
                size="small"
                aria-label={t('common.delete')}
                onClick={() => handleRemoveProductFromPlan(p.sku ?? '')}
                disabled={false}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !plan) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/plans')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/plans')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {plan.name}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <TextField
            sx={detailFieldSx}
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />

          <TextField
            sx={detailFieldSx}
            label={t('masterData.code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('masterData.termMonths')}
            value={contractTermMonths}
            onChange={(e) => setContractTermMonths(e.target.value)}
            type="number"
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.net')}
            value={amountNet}
            onChange={(e) => setAmountNet(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.gross')}
            value={amountGross}
            onChange={(e) => setAmountGross(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(plan.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(plan.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          {superAdmin && (
            <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {t('common.save')}
              </Button>
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('masterData.linkedProducts')}
        </Typography>
        {superAdmin && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label={t('masterData.productId')}
              value={productIdToAdd}
              onChange={(e) => setProductIdToAdd(e.target.value)}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: 0, sm: 200 } }}
            />
            <Button
              variant="outlined"
              onClick={handleAddProductToPlan}
              disabled={saving || !productIdToAdd.trim()}
            >
              {t('masterData.linkProduct')}
            </Button>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t('masterData.planProductDeployHint')}
        </Typography>
        <ResponsiveRowTableCards
          columns={productColumns}
          rows={(productsData?.products?.nodes as Product[]) ?? []}
          getRowKey={(p) => p.id}
          minTableWidth={640}
          emptyContent={
            <Typography variant="body2" color="text.secondary">
              {t('common.noData')}
            </Typography>
          }
        />
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('masterData.confirmDeletePlan')}</DialogTitle>
        <DialogContent>
          <Typography>{t('masterData.confirmDeleteBody')}</Typography>
        </DialogContent>
        {/* <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions> */}
      </Dialog>
    </Box>
  );
}
